import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { ServiceLeadForm } from '@/components/ServiceLeadForm';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, ExternalLink, ArrowRight, Camera, DollarSign, Users, Sparkles, Star, Check, TrendingUp, Shield, Clock, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';

// Images premium - Appartements Airbnb modernes et contemporains
const HERO_IMAGE = "https://images.unsplash.com/photo-1761783066826-689af9f13267?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxhaXJibmIlMjBsdXh1cnklMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMG1vZGVybnxlbnwwfHx8fDE3NzQxMjkwNTd8MA&ixlib=rb-4.1.0&q=85";
const CONTENT_IMAGE_1 = "https://images.unsplash.com/photo-1713365963723-655fa464b681?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxhaXJibmIlMjBsdXh1cnklMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMG1vZGVybnxlbnwwfHx8fDE3NzQxMjkwNTd8MA&ixlib=rb-4.1.0&q=85";
const CONTENT_IMAGE_2 = "https://images.unsplash.com/photo-1715985160053-d339e8b6eb94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxhaXJibmIlMjBsdXh1cnklMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMG1vZGVybnxlbnwwfHx8fDE3NzQxMjkwNTd8MA&ixlib=rb-4.1.0&q=85";

export const ConciergeriePage = () => {
  const questions = [
    { id: 'bien_type', label: 'Type de bien ?', type: 'select', options: ['Appartement', 'Chalet', 'Villa', 'Studio', 'Maison'] },
    { id: 'localisation', label: 'Localisation ?', type: 'select', options: ['Genève', 'Lausanne', 'Montreux', 'Verbier', 'Crans-Montana', 'Villars', 'Autre'] },
    { id: 'chambres', label: 'Nombre de chambres ?', type: 'select', options: ['Studio', '1 chambre', '2 chambres', '3 chambres', '4+ chambres'] },
    { id: 'deja_loue', label: 'Déjà en location saisonnière ?', type: 'select', options: ['Oui, je veux optimiser', 'Non, je veux commencer', 'J\'hésite encore'] },
    { id: 'gestion', label: 'Gestion souhaitée ?', type: 'select', options: ['Complète (clé en main)', 'Partielle', 'Je veux comprendre les options'] }
  ];

  const cities = [
    { name: 'Genève', slug: 'geneve', type: 'Ville' },
    { name: 'Lausanne', slug: 'lausanne', type: 'Ville' },
    { name: 'Montreux', slug: 'montreux', type: 'Riviera' },
    { name: 'Verbier', slug: 'verbier', type: 'Station' },
    { name: 'Crans-Montana', slug: 'crans-montana', type: 'Station' },
    { name: 'Villars', slug: 'villars', type: 'Station' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* ============================================
          PILIER 1: ATTENTION - Hero immersif
          ============================================ */}
      <section className="hero-section relative min-h-[70vh] sm:min-h-[80vh] flex items-center">
        <img src={HERO_IMAGE} alt="Chalet de luxe avec vue sur les Alpes suisses" className="hero-image-bg" />
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 py-10 sm:py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <Building2 className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 text-white" />
              <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm text-xs sm:text-sm">
                Homely Conciergerie
              </Badge>
            </div>
            
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 text-white leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              La conciergerie Airbnb n°1 en Suisse romande
            </h1>
            
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-4 sm:mb-6 max-w-2xl">
              Confiez votre bien à des experts locaux. Nous gérons tout : annonce optimisée, 
              pricing dynamique, accueil voyageurs, ménage. Vous encaissez, nous travaillons.
            </p>

            {/* Key metrics */}
            <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4 mb-5 sm:mb-8">
              <div className="flex items-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
                <span className="text-white text-xs sm:text-sm font-semibold">+35% de revenus vs auto-gestion</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <Star className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-400" />
                <span className="text-white text-xs sm:text-sm">4.9/5 note moyenne voyageurs</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 bg-white/10 backdrop-blur-sm px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
                <span className="text-white text-xs sm:text-sm">Assurance RC en option</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button 
                size="lg" 
                className="btn-premium h-12 sm:h-14 px-6 sm:px-10 text-sm sm:text-base rounded-full w-full sm:w-auto"
                onClick={() => document.getElementById('form').scrollIntoView({ behavior: 'smooth' })}
              >
                Estimer mes revenus gratuit
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="h-12 sm:h-14 px-6 sm:px-8 text-sm sm:text-base rounded-full bg-white/10 border-white/30 text-white hover:bg-white hover:text-[var(--anthracite)] backdrop-blur-sm gap-2 w-full sm:w-auto"
                onClick={() => window.open('https://www.homely-conciergerie.ch', '_blank')}
              >
                <ExternalLink className="h-4 w-4" />
                homely-conciergerie.ch
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 2: ÉMOTION - Le problème
          ============================================ */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-orange-100 text-orange-700">Le constat</Badge>
              
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Gérer un Airbnb seul, c'est un second emploi
              </h2>
              
              <p className="text-lg text-muted-foreground mb-6">
                Messages à toute heure, ménage entre deux réservations, pricing à ajuster, 
                problèmes à résoudre... La location courte durée peut vite devenir épuisante 
                et empiéter sur votre vie personnelle.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { icon: Clock, title: '15-20h/mois', desc: 'Temps moyen passé à gérer un seul bien en auto-gestion' },
                  { icon: '😰', title: 'Stress permanent', desc: 'Répondre aux messages 24/7, même en vacances' },
                  { icon: '📉', title: 'Revenus sous-optimisés', desc: 'Sans pricing dynamique, vous perdez 20-40% de revenus' },
                  { icon: '⭐', title: 'Notes qui baissent', desc: 'Un détail oublié = mauvais avis = moins de réservations' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 bg-secondary/30 rounded-lg">
                    {typeof item.icon === 'string' ? (
                      <span className="text-2xl">{item.icon}</span>
                    ) : (
                      <item.icon className="h-6 w-6 text-orange-500 flex-shrink-0 mt-1" />
                    )}
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-lg font-semibold" style={{ color: 'var(--sage-dark)' }}>
                Et si vous pouviez garder les revenus sans les contraintes ?
              </p>
            </div>

            <div className="relative">
              <img 
                src={CONTENT_IMAGE_1} 
                alt="Intérieur luxueux appartement Airbnb" 
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-lg max-w-xs">
                <p className="font-bold text-3xl" style={{ color: 'var(--sage)' }}>+35%</p>
                <p className="text-sm text-muted-foreground">de revenus en moyenne avec notre gestion</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 3: MÉMOIRE - Notre solution complète
          ============================================ */}
      <section className="py-20 gradient-premium">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>Gestion clé en main</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Tout ce dont votre bien a besoin pour performer
            </h2>
            <div className="section-divider"></div>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Une équipe dédiée gère chaque aspect de votre location. Vous restez propriétaire, 
              nous nous occupons de tout le reste.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              { icon: Camera, title: 'Photos & Annonce', desc: 'Shooting pro, rédaction optimisée, multi-plateformes (Airbnb, Booking, VRBO)' },
              { icon: DollarSign, title: 'Pricing dynamique', desc: 'Algorithme qui ajuste les prix selon saison, événements, demande' },
              { icon: Users, title: 'Accueil voyageurs', desc: 'Check-in/out, communication 24/7, gestion des demandes' },
              { icon: Sparkles, title: 'Ménage & Linge', desc: 'Nettoyage pro entre chaque séjour, linge hôtelier fourni' }
            ].map((service, idx) => (
              <div key={idx} className="card-premium p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>
                  <service.icon className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                <p className="text-muted-foreground">{service.desc}</p>
              </div>
            ))}
          </div>

          {/* Image + Liste avantages */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <img 
              src={CONTENT_IMAGE_2} 
              alt="Vue panoramique montagne suisse" 
              className="rounded-2xl shadow-xl w-full"
            />
            <div>
              <h3 className="text-2xl font-bold mb-6">Ce qui est inclus dans notre gestion :</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  'Création annonce optimisée',
                  'Photos professionnelles',
                  'Pricing dynamique IA',
                  'Gestion calendrier',
                  'Communication voyageurs 24/7',
                  'Check-in/Check-out',
                  'Ménage professionnel',
                  'Linge hôtelier',
                  'Petite maintenance',
                  'Reporting mensuel',
                  'Assurance RC (en option)',
                  'Gestion des avis'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 4: DÉCISION - Preuves et chiffres
          ============================================ */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          
          {/* Stats bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 p-8 rounded-2xl mb-16" style={{ backgroundColor: 'var(--sage)' }}>
            {[
              { value: '150+', label: 'Biens en gestion' },
              { value: '98%', label: 'Taux d\'occupation' },
              { value: '+35%', label: 'Revenus vs auto-gestion' },
              { value: '4.9/5', label: 'Note voyageurs' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center text-white">
                <p className="text-4xl font-bold mb-1">{stat.value}</p>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Témoignages */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Ils nous font confiance
            </h2>
            <div className="section-divider"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              { 
                name: 'Jean-Pierre M.', 
                city: 'Verbier', 
                bien: 'Chalet 4 chambres',
                text: 'Depuis que Homely gère mon chalet, mes revenus ont augmenté de 40% et je n\'ai plus aucun stress. Je recommande les yeux fermés !', 
                revenue: '85\'000 CHF/an'
              },
              { 
                name: 'Catherine S.', 
                city: 'Montreux', 
                bien: 'Appartement vue lac',
                text: 'Équipe réactive et professionnelle. Mes voyageurs sont ravis, les avis sont excellents. Un vrai partenariat gagnant-gagnant.', 
                revenue: '42\'000 CHF/an'
              },
              { 
                name: 'Marc & Sophie L.', 
                city: 'Crans-Montana', 
                bien: '2 appartements',
                text: 'On hésitait à se lancer dans la location saisonnière. Homely nous a accompagnés du début à la fin. Résultat : 2 biens qui tournent à plein !', 
                revenue: '68\'000 CHF/an'
              }
            ].map((review, idx) => (
              <div key={idx} className="card-premium p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4 italic">"{review.text}"</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-[var(--sage)] flex items-center justify-center text-white font-bold">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{review.name}</p>
                      <p className="text-xs text-muted-foreground">{review.bien} • {review.city}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-sm" style={{ color: 'var(--sage)' }}>{review.revenue}</p>
                    <p className="text-xs text-muted-foreground">revenus annuels</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Zones d'intervention */}
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-4">Nos zones d'intervention</h3>
            <p className="text-muted-foreground mb-6">Nous gérons des biens dans les destinations les plus prisées de Suisse romande</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {cities.map((city, idx) => (
              <Link 
                key={idx}
                to={`/conciergerie/${city.slug}`}
                className="card-premium p-4 text-center hover:border-[var(--sage)] transition-colors"
              >
                <p className="font-semibold">{city.name}</p>
                <p className="text-xs text-muted-foreground">{city.type}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          PILIER 5: ACTION - Formulaire
          ============================================ */}
      <section className="py-20" style={{ backgroundColor: 'var(--anthracite)' }} id="form">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="text-white">
              <Badge className="mb-4 bg-[var(--sage)]">Estimation gratuite</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Découvrez le potentiel de votre bien
              </h2>
              <p className="text-white/70 mb-8 text-lg">
                Recevez une estimation personnalisée de vos revenus locatifs potentiels 
                et un plan d'action pour maximiser votre rentabilité.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  'Estimation de revenus basée sur des données réelles',
                  'Analyse de votre bien et de son potentiel',
                  'Recommandations personnalisées',
                  'Sans engagement, 100% gratuit'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-[var(--sage)] flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <ServiceLeadForm service="louer" pole="conciergerie" questions={questions} variant="ultra" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section for SEO */}
      <section className="py-16 border-t">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold mb-8 text-center">Questions fréquentes</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              { q: 'Combien coûte la gestion Homely Conciergerie ?', a: 'Notre commission varie entre 15% et 25% selon le niveau de service choisi et le type de bien. Pas de frais fixes, vous ne payez que sur les revenus générés.' },
              { q: 'Quelle est votre zone d\'intervention ?', a: 'Nous gérons des biens à Genève, Lausanne, Montreux, Verbier, Crans-Montana et Villars. Contactez-nous pour d\'autres localités.' },
              { q: 'Puis-je utiliser mon bien quand je veux ?', a: 'Absolument ! Vous bloquez les dates souhaitées dans le calendrier et nous gérons le reste autour de vos disponibilités.' },
              { q: 'Comment se passe la mise en route ?', a: 'Nous visitons votre bien, réalisons le shooting photo, créons les annonces et vous êtes prêt à recevoir en 7-10 jours.' }
            ].map((faq, idx) => (
              <div key={idx} className="p-6 bg-secondary/20 rounded-lg">
                <h3 className="font-semibold mb-2">{faq.q}</h3>
                <p className="text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
